import React from "react";
import { useState } from "react";
import Switch from "../UI/Switch";

export const FIELD =
  "mb-3 w-full rounded-lg border border-stone-300 bg-stone-100 p-3 text-stone-500 placeholder:text-stone-400 dark:border-slate-600 dark:bg-slate-800 dark:text-white";
export const LABEL = "text-sm font-medium text-stone-700 dark:text-slate-300";

const today = () => new Date().toISOString().substring(0, 10);

/** Farmers type a comma; the API wants a dot, and an empty field means "unset". */
const toNumber = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = parseFloat(String(value).replace(",", "."));
  return Number.isNaN(parsed) ? null : parsed;
};
const asDateInput = (value) => (value ? String(value).substring(0, 10) : "");

export const BREEDS = [
  "Holstein / Red Holstein",
  "Swiss Fleckvieh",
  "Montbéliard",
  "Jersey",
  "Brown Swiss",
  "À viande",
  "Autre",
];

const DEFAULTS = {
  bdta: "",
  name: "",
  sex: "f",
  breed: "",
  purpose: "",
  target_age_months: "",
  weight_method: "mesure",
  castration: false,
  birthdate: today(),
  birthtime: "",
  birthPlace: "",
  vitality: "vif",
  birthtype: "sans aide",
  weight: "",
  colostrum: "",
  apporttime: "",
  colostrumMean: "",
  colostrumSource: "",
  colostrumQuality: "",
  colostrumContent_fer: false,
  colostrumContent_selenium: false,
  colostrumContent_locatim: false,
  milk_type: "",
  milk_density: "",
  milk_price: "",
  vaccinated: false,
  vaccine: "",
  ecornage: false,
  ecornageDate: today(),
};

/**
 * Every field of a veau in one place.
 *
 * The four screens that edit a calf — the mobile wizard, the mobile edit page,
 * and the two desktop dialogs — all drive this, so a new field is declared once
 * and shows up in all of them.
 */
export function useCalfForm(initial) {
  const [values, setValues] = useState({ ...DEFAULTS, ...(initial || {}) });

  const set = (key) => (value) =>
    setValues((current) => ({ ...current, [key]: value }));

  /** Fills the form from a record loaded off the API. */
  const load = (calf) =>
    setValues((current) => ({
      ...current,
      ...calf,
      birthdate: asDateInput(calf.birthdate) || today(),
      ecornageDate: asDateInput(calf.ecornageDate) || today(),
      castration: Boolean(calf.castration),
      ecornage: Boolean(calf.ecornage),
      colostrumContent_fer: Boolean(calf.colostrumContent_fer),
      colostrumContent_selenium: Boolean(calf.colostrumContent_selenium),
      colostrumContent_locatim: Boolean(calf.colostrumContent_locatim),
    }));

  /** The shape the calf endpoints expect. */
  const toPayload = () => ({
    bdta: values.bdta,
    name: values.name,
    sex: values.sex,
    // only males can be castrated, so never send a stale true for a female
    castration: values.sex === "m" ? values.castration : false,
    birthdate: values.birthdate,
    birthtime: values.birthtime,
    birthPlace: values.birthPlace,
    vitality: values.vitality,
    birthtype: values.birthtype,
    colostrum: values.colostrum,
    apporttime: values.apporttime,
    colostrumMean: values.colostrumMean,
    colostrumSource: values.colostrumSource,
    colostrumQuality: values.colostrumQuality,
    colostrumContent_fer: values.colostrumContent_fer,
    colostrumContent_selenium: values.colostrumContent_selenium,
    colostrumContent_locatim: values.colostrumContent_locatim,
    breed: values.breed || null,
    purpose: values.purpose || null,
    // the objective only means something once a purpose is chosen
    target_age_months: values.purpose ? toNumber(values.target_age_months) : null,
    weight_method: values.weight_method || null,
    milk_type: values.milk_type || null,
    // density is only asked for powder
    milk_density:
      values.milk_type === "poudre" ? toNumber(values.milk_density) : null,
    milk_price: toNumber(values.milk_price),
    ecornage: values.ecornage,
    ecornageDate: values.ecornage ? values.ecornageDate : null,
  });

  return { values, set, load, toPayload };
}

const Text = ({ form, name, label, placeholder, type = "text" }) => (
  <>
    <label htmlFor={name + "_input"} className={LABEL}>
      {label}
    </label>
    <input
      className={FIELD}
      type={type}
      value={form.values[name] ?? ""}
      onChange={(e) => form.set(name)(e.target.value)}
      placeholder={placeholder}
      name={name + "_input"}
    />
  </>
);

const Select = ({ form, name, label, options }) => (
  <>
    <label htmlFor={name + "_input"} className={LABEL}>
      {label}
    </label>
    <select
      className={FIELD}
      value={form.values[name] ?? ""}
      onChange={(e) => form.set(name)(e.target.value)}
      name={name + "_input"}
    >
      {options.map(([value, text]) => (
        <option key={value} value={value}>
          {text}
        </option>
      ))}
    </select>
  </>
);

const Toggle = ({ form, name, label }) => (
  <div className="mb-3 flex flex-row items-center justify-between">
    <span className={LABEL}>{label}</span>
    <Switch checked={form.values[name]} onChange={form.set(name)} />
  </div>
);

export function CalfIdentityFields({ form }) {
  return (
    <>
      <Text form={form} name="bdta" label="Numéro BDTA" placeholder="Numéro BDTA" />
      <Text form={form} name="name" label="Nom" placeholder="Nom du veau" />
      <Select
        form={form}
        name="sex"
        label="Sexe"
        options={[["f", "Femelle"], ["m", "Mâle"]]}
      />
      {form.values.sex === "m" && (
        <Toggle form={form} name="castration" label="Castration" />
      )}
      <Select
        form={form}
        name="breed"
        label="Race"
        options={[["", "Choisir une race"], ...BREEDS.map((b) => [b, b])]}
      />
      <Select
        form={form}
        name="purpose"
        label="Future utilisation"
        options={[
          ["", "Choisir une utilisation"],
          ["elevage", "Élevage"],
          ["engraissement", "Engraissement"],
        ]}
      />
      {/* The objective that follows depends on which purpose was chosen. */}
      {form.values.purpose === "elevage" && (
        <Text
          form={form}
          name="target_age_months"
          label="Objectif d'âge au premier vêlage (mois)"
          placeholder="Nombre de mois"
        />
      )}
      {form.values.purpose === "engraissement" && (
        <Text
          form={form}
          name="target_age_months"
          label="Objectif d'âge à la vente (mois)"
          placeholder="Nombre de mois"
        />
      )}
    </>
  );
}

export function CalfBirthFields({ form }) {
  return (
    <>
      <Text form={form} name="birthdate" label="Date de naissance" type="date" />
      <Text form={form} name="birthtime" label="Heure de naissance" type="time" />
      <Select
        form={form}
        name="birthPlace"
        label="Lieu de naissance"
        options={[
          ["", "Choisir un lieu"],
          ["box de vêlage", "Box de vêlage"],
          ["prairie", "Prairie"],
          ["au sein du troupeau", "Au sein du troupeau"],
          ["autre", "Autre"],
        ]}
      />
      <Select
        form={form}
        name="vitality"
        label="Vitalité"
        options={[["vif", "Vif"], ["apathique", "Apathique"], ["mort", "Mort"]]}
      />
      <Select
        form={form}
        name="birthtype"
        label="Type de naissance"
        options={[
          ["sans aide", "Sans aide"],
          ["aide faible", "Aide faible"],
          ["naissance difficile", "Naissance difficile"],
        ]}
      />
    </>
  );
}

/** Only offered when creating: afterwards, weight is its own list of weighings. */
export function CalfBirthWeightField({ form }) {
  return (
    <Text
      form={form}
      name="weight"
      label="Poids à la naissance (kg)"
      placeholder="Poids en kilos"
    />
  );
}

/** How this calf is weighed — the same method from birth until it leaves. */
export function CalfWeightMethodField({ form }) {
  return (
    <Select
      form={form}
      name="weight_method"
      label="Méthode de pesée"
      options={[["mesure", "Mesuré"], ["estime", "Estimé"]]}
    />
  );
}

export function CalfColostrumFields({ form }) {
  return (
    <>
      <Text
        form={form}
        name="colostrum"
        label="Quantité de colostrum (l)"
        placeholder="Quantité en litres"
      />
      <Text form={form} name="apporttime" label="Heure de l'apport" type="time" />
      <Select
        form={form}
        name="colostrumMean"
        label="Moyen d'apport"
        options={[["", "Choisir un moyen"], ["Biberon", "Biberon"], ["Sonde", "Sonde"]]}
      />
      <Select
        form={form}
        name="colostrumSource"
        label="Source du colostrum"
        options={[
          ["", "Choisir une source"],
          ["Maternel", "Maternel"],
          ["Congelé", "Congelé"],
          ["Autre", "Autre"],
        ]}
      />
      <Select
        form={form}
        name="colostrumQuality"
        label="Qualité du colostrum"
        options={[
          ["", "Choisir une qualité"],
          ["Bon", "Bon (>23% BRIX)"],
          ["Moyen", "Moyen (17-23% BRIX)"],
          ["Mauvais", "Mauvais (<17% BRIX)"],
        ]}
      />
      <Toggle form={form} name="colostrumContent_fer" label="Fer" />
      <Toggle form={form} name="colostrumContent_selenium" label="Sélénium" />
      <Toggle form={form} name="colostrumContent_locatim" label="Locatim" />
    </>
  );
}

/**
 * Allaitement, the section the feedback asks for under colostrum.
 *
 * The type and its price hold for the calf's whole nursing, so they live on the
 * calf rather than on each period. The price unit follows the type, and the
 * conversion behind it is the farmer's to do — deliberately, for now.
 */
export function CalfFeedingFields({ form }) {
  const priceLabel =
    form.values.milk_type === "poudre"
      ? "Prix du lait (francs / dt)"
      : "Prix du lait (centimes / kg)";

  return (
    <>
      <Select
        form={form}
        name="milk_type"
        label="Type de lait"
        options={[
          ["", "Choisir un type"],
          ["entier", "Lait entier"],
          ["poudre", "Poudre de lait"],
          ["mixte", "Mixte (lait entier + poudre)"],
        ]}
      />
      {form.values.milk_type === "poudre" && (
        <Text
          form={form}
          name="milk_density"
          label="Densité (grammes / litre)"
          placeholder="Densité en g/l"
        />
      )}
      {form.values.milk_type && (
        <Text
          form={form}
          name="milk_price"
          label={priceLabel}
          placeholder="Prix"
        />
      )}
    </>
  );
}

export function CalfFollowUpFields({ form, showVaccine = true }) {
  return (
    <>
      {showVaccine && (
        <>
          <Toggle form={form} name="vaccinated" label="Vacciné" />
          {form.values.vaccinated && (
            <Text
              form={form}
              name="vaccine"
              label="Nom du vaccin"
              placeholder="Nom du vaccin"
            />
          )}
        </>
      )}
      <Toggle form={form} name="ecornage" label="Écornage" />
      {form.values.ecornage && (
        <Text form={form} name="ecornageDate" label="Date d'écornage" type="date" />
      )}
    </>
  );
}
